import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import type { ClientSlidingScaleView, SlidingMode, SlidingTier } from '@ehr/shared';
import { SLIDING_EDIT_ROLES, addYearIso, clientSlidingScaleSchema, tierLabel } from '@ehr/shared';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../auth/session.js';
import { Dd } from './Dropdown.js';
import { DateField } from './DateTimeField.js';
import { Modal } from './Modal.js';
import {
  BLANK_SLIDING_DRAFT,
  SLIDING_TIERS_CHANGED,
  amountNote,
  backToTier,
  bodyFrom,
  draftErrors,
  draftFrom,
  onInvoicesLabel,
  withAmount,
  withMode,
  withTier,
  type SlidingDraft,
  type SlidingDraftField,
} from './slidingDraft.js';

/**
 * ehr#375 - the sliding scale (Chase's SLIDING_SCALE_10-12-V1 handoff; Jay 10-13: build the EHR half).
 *   - useSlidingScale + SlidingScaleSection: a client's sliding scale in Demographics → Insurance /
 *     Payor, after "+ Add secondary insurance". The drawer owns the draft (it survives switching
 *     sections), counts it in "unsaved changes" and saves it with Save draft / Save record.
 *     Jay 10-30: it reads like the practice's tier table (Tier, Discount type, Amount); picking a
 *     tier fills the type and amount from Data & settings, and a changed amount is this client's own
 *     (what invoices use). See slidingDraft.ts.
 *   - SlidingTiersSettings: the practice's tiers (Manage settings → Data & settings → Sliding scale).
 * The chart is the only place a client's sliding scale is set; invoices read it (GET ?date=, or the
 * read-only view client_sliding_scale_effective that RCM reads).
 */

/** A local YYYY-MM-DD (today, or the day a History timestamp falls on here). */
const localIso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayIso = () => localIso(new Date());
const us = (iso: string) => `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}`;
const errText = (e: unknown, fallback: string) =>
  e instanceof ApiError && e.message ? e.message : fallback;

export type SlidingScaleState = ReturnType<typeof useSlidingScale>;

/** A client's sliding scale: loaded once per client, edited as a draft, saved on the drawer's Save. */
export function useSlidingScale(contactId: string, enabled: boolean) {
  const [view, setView] = useState<ClientSlidingScaleView | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'forbidden' | 'error'>('loading');
  const [draft, setDraft] = useState<SlidingDraft>(BLANK_SLIDING_DRAFT);
  const [dirty, setDirty] = useState(false);
  const [errs, setErrs] = useState<Partial<Record<SlidingDraftField, string>>>({});
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;

  useEffect(() => {
    if (!enabled) return;
    let live = true;
    setStatus('loading');
    const load = (first: boolean) =>
      api
        .get<ClientSlidingScaleView>(`/contacts/${contactId}/sliding-scale`)
        .then((v) => {
          if (!live) return;
          setView(v);
          // ehr#375 (Jay 10-30): tiers saved in Data & settings fill in here at once, unless this
          // chart has unsaved sliding scale edits (those stay as typed).
          if (first || !dirtyRef.current) {
            setDraft(draftFrom(v.scale, v.tiers));
            setDirty(false);
            setErrs({});
          }
          setStatus('ready');
        })
        .catch((e: unknown) => {
          if (live && first)
            setStatus(e instanceof ApiError && e.status === 403 ? 'forbidden' : 'error');
        });
    void load(true);
    const onTiers = () => void load(false);
    window.addEventListener(SLIDING_TIERS_CHANGED, onTiers);
    return () => {
      live = false;
      window.removeEventListener(SLIDING_TIERS_CHANGED, onTiers);
    };
  }, [contactId, enabled]);

  const tiers = view?.tiers ?? [];
  const change = (next: (d: SlidingDraft) => SlidingDraft, fields: SlidingDraftField[]) => {
    setDraft(next);
    setDirty(true);
    setErrs((e) => {
      const n = { ...e };
      for (const k of fields) delete n[k];
      return n;
    });
  };
  const pickTier = (id: string) =>
    change((d) => withTier(d, tiers, id, todayIso()), ['amount', 'effective', 'reviewBy']);
  const typeAmount = (v: string) =>
    change((d) => withAmount(d, v, todayIso()), ['amount', 'effective', 'reviewBy']);
  const pickMode = (m: SlidingMode) => change((d) => withMode(d, m), ['amount']);
  const useTierAmount = () => change((d) => backToTier(d, tiers), ['amount']);
  const setDate = (k: 'effective' | 'reviewBy', v: string) =>
    change(
      (d) => {
        const n = { ...d, [k]: v };
        // A new Effective date with no Review by fills Review by a year later.
        if (k === 'effective' && v && !n.reviewBy) n.reviewBy = addYearIso(v);
        return n;
      },
      [k],
    );

  /** Marks the bad fields red; returns the first problem, or null. */
  const validate = (): string | null => {
    const r = clientSlidingScaleSchema.safeParse(bodyFrom(draft, tiers));
    if (r.success) {
      setErrs({});
      return null;
    }
    setErrs(draftErrors(r.error.issues));
    return r.error.issues[0]?.message ?? 'Check the sliding scale';
  };

  const save = async (): Promise<string | null> => {
    if (!dirty) return null;
    const bad = validate();
    if (bad) return bad;
    try {
      const v = await api.put<ClientSlidingScaleView>(
        `/contacts/${contactId}/sliding-scale`,
        bodyFrom(draft, tiers),
      );
      setView(v);
      setDraft(draftFrom(v.scale, v.tiers));
      setDirty(false);
      return null;
    } catch (e) {
      return errText(e, 'Could not save the sliding scale');
    }
  };

  const revert = () => {
    setDraft(draftFrom(view?.scale ?? null, tiers));
    setDirty(false);
    setErrs({});
  };

  return {
    view,
    status,
    draft,
    dirty,
    errs,
    pickTier,
    typeAmount,
    pickMode,
    useTierAmount,
    setDate,
    validate,
    save,
    revert,
  };
}

const MODE_OPTS = [
  { value: 'pct', label: 'Percent off (%)' },
  { value: 'usd', label: 'Dollar amount off ($)' },
];

/** Demographics → Insurance / Payor: the client's sliding scale. */
export function SlidingScaleSection({ ss }: { ss: SlidingScaleState }) {
  if (ss.status === 'loading') return null;
  if (ss.status === 'forbidden') return null;
  if (ss.status === 'error')
    return (
      <div className="ss-sec">
        <h5 className="ss-head">Sliding scale</h5>
        <p className="muted">Could not load the sliding scale.</p>
      </div>
    );
  const { view, draft: d, errs } = ss;
  const tiers = view?.tiers ?? [];
  const ro = !view?.canEdit;
  const today = todayIso();
  const due = !!d.reviewBy && today > d.reviewBy;
  const note = amountNote(d, tiers);
  return (
    <div className="ss-sec" data-ss-section>
      <h5 className="ss-head">Sliding scale</h5>
      <p className="ss-now">
        <span className="ss-now-k">On invoices:</span> <b>{onInvoicesLabel(d, tiers)}</b>
      </p>
      {ro && (
        <p className="muted ss-ro">
          Only an admin or manager can change a client&apos;s sliding scale.
        </p>
      )}
      {/* Jay 10-30: the practice's tier columns (Tier, Discount type, Amount), filled from the tier */}
      <div className="demo-grid ss-grid ss-grid3">
        <div className="demo-field" data-ss="tier">
          <span className="demo-field-label">Tier</span>
          <Dd
            value={d.tierId}
            searchable={false}
            disabled={ro}
            ariaLabel="Sliding scale tier"
            options={[{ value: '', label: 'None (full fee)' }].concat(
              tiers.map((t) => ({ value: t.id, label: tierLabel(t) })),
            )}
            onChange={ss.pickTier}
          />
          {!tiers.length && !ro && (
            <span className="ss-help">
              Add tiers in Manage settings → Data &amp; settings → Sliding scale.
            </span>
          )}
        </div>
        <div className="demo-field" data-ss="mode">
          <span className="demo-field-label">Discount type</span>
          <Dd
            value={d.mode}
            searchable={false}
            disabled={ro}
            ariaLabel="Discount type"
            options={MODE_OPTS}
            onChange={(v) => ss.pickMode(v as SlidingMode)}
          />
        </div>
        <div className={`demo-field${errs.amount ? ' invalid' : ''}`} data-ss="amount">
          <span className="demo-field-label" id="ss-own-amount-label">
            Amount
          </span>
          <input
            id="ss-own-amount"
            aria-labelledby="ss-own-amount-label"
            inputMode="decimal"
            autoComplete="off"
            value={d.amount}
            disabled={ro}
            placeholder={d.mode === 'usd' ? '$0.00' : '%'}
            onChange={(e) => ss.typeAmount(e.target.value)}
          />
          {errs.amount ? (
            <span className="demo-field-err">{errs.amount}</span>
          ) : note ? (
            <span className="ss-help" data-ss-note>
              {note.text}
              {note.reset && !ro && (
                <>
                  {' '}
                  <button type="button" className="ss-link" onClick={ss.useTierAmount}>
                    {note.reset}
                  </button>
                </>
              )}
            </span>
          ) : null}
        </div>
      </div>
      <div className="demo-grid ss-grid">
        <div className={`demo-field${errs.effective ? ' invalid' : ''}`} data-ss="effective">
          <span className="demo-field-label">Effective date</span>
          <DateField
            value={d.effective}
            disabled={ro}
            onChange={(v) => ss.setDate('effective', v)}
          />
          {errs.effective && <span className="demo-field-err">{errs.effective}</span>}
        </div>
        <div
          className={`demo-field${errs.reviewBy ? ' invalid' : ''}${due ? ' ss-due' : ''}`}
          data-ss="reviewBy"
        >
          <span className="demo-field-label">Review by</span>
          <DateField value={d.reviewBy} disabled={ro} onChange={(v) => ss.setDate('reviewBy', v)} />
          {errs.reviewBy ? (
            <span className="demo-field-err">{errs.reviewBy}</span>
          ) : due ? (
            <span className="demo-field-err">Review was due {us(d.reviewBy)}.</span>
          ) : null}
        </div>
      </div>
      <h6 className="ss-sub">History</h6>
      {view?.history.length ? (
        <table className="ss-hist">
          <thead>
            <tr>
              <th>Date</th>
              <th>By</th>
              <th>Change</th>
            </tr>
          </thead>
          <tbody>
            {view.history.map((h, i) => (
              <tr key={i}>
                <td>{us(localIso(new Date(h.at)))}</td>
                <td>{h.by}</td>
                <td>{h.what}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="muted">No changes yet.</p>
      )}
    </div>
  );
}

/** Focus the first sliding-scale field that's marked red (after the section is on screen). */
export function focusSlidingError() {
  setTimeout(() => {
    const el = document.querySelector<HTMLElement>('[data-ss-section] .invalid');
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.querySelector<HTMLElement>('input, button')?.focus({ preventScroll: true });
  }, 80);
}

/* ─────────────────────────── Tier setup ─────────────────────────── */

interface TierRow {
  key: string;
  id?: string;
  name: string;
  mode: SlidingMode;
  amount: string;
  clients: number;
}
let rowSeq = 0;
const toRows = (ts: SlidingTier[]): TierRow[] =>
  ts.map((t) => ({
    key: t.id,
    id: t.id,
    name: t.name,
    mode: t.mode,
    amount: String(t.amount),
    clients: t.clients ?? 0,
  }));

/**
 * Manage settings → Data & settings → Sliding scale: the practice's tiers (the mockup's Tier setup).
 * `toast` shows save and error messages; `onDirty` and `saveRef` let Data & settings ask before it
 * closes over unsaved tiers ("You have unsaved changes", Save changes / Leave without saving).
 */
export function SlidingTiersSettings({
  toast,
  onDirty,
  saveRef,
}: {
  toast?: (msg: string) => void;
  onDirty?: (dirty: boolean) => void;
  saveRef?: MutableRefObject<(() => Promise<boolean>) | null>;
} = {}) {
  const { user } = useSession();
  const canEdit = !!user && (SLIDING_EDIT_ROLES as readonly string[]).includes(user.role);
  const [saved, setSaved] = useState<SlidingTier[] | null>(null);
  const [rows, setRows] = useState<TierRow[]>([]);
  const [dirty, setDirty] = useState(false);
  const [errs, setErrs] = useState<Record<string, { name?: string; amount?: string }>>({});
  const [loadErr, setLoadErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<{
    row: TierRow;
    to: string;
    names: string[] | null;
  } | null>(null);
  const say = (m: string) => toast?.(m);
  const [listing, setListing] = useState<{
    name: string;
    clients: { contactId: string; name: string; reviewBy: string | null }[] | null;
  } | null>(null);

  const reset = (ts: SlidingTier[]) => {
    setSaved(ts);
    setRows(toRows(ts));
    setDirty(false);
    setErrs({});
  };
  const load = () => {
    setLoadErr('');
    api
      .get<SlidingTier[]>('/sliding-scale/tiers')
      .then(reset)
      .catch((e: unknown) => setLoadErr(errText(e, 'Could not load the sliding scale tiers')));
  };
  useEffect(load, []);

  useEffect(() => onDirty?.(dirty), [dirty, onDirty]);
  const touch = (next: TierRow[]) => {
    setRows(next);
    setDirty(true);
  };
  const set = (key: string, patch: Partial<TierRow>) => {
    touch(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    setErrs((e) => {
      const n = { ...e };
      if (n[key])
        n[key] = {
          ...n[key],
          ...Object.fromEntries(Object.keys(patch).map((k) => [k, undefined])),
        };
      return n;
    });
  };
  const add = () => {
    const key = `new-${++rowSeq}`;
    // The first "Tier N" no row has (after a removal, rows.length + 1 can be taken).
    const taken = new Set(rows.map((r) => r.name.trim().toLowerCase()));
    let n = rows.length + 1;
    while (taken.has(`tier ${n}`)) n++;
    touch([...rows, { key, name: `Tier ${n}`, mode: 'pct', amount: '', clients: 0 }]);
    setTimeout(() => document.getElementById(`ss-tier-amt-${key}`)?.focus(), 30);
  };
  const remove = (r: TierRow) => {
    if (r.id && r.clients > 0) {
      setRemoving({ row: r, to: '', names: null });
      // the window names them: "2 clients are on Tier 1: Maya Torres, Avery Chen."
      api
        .get<{ name: string }[]>(`/sliding-scale/tiers/${r.id}/clients`)
        .then((c) =>
          setRemoving((m) =>
            m && m.row.key === r.key ? { ...m, names: c.map((x) => x.name) } : m,
          ),
        )
        .catch(() => setRemoving((m) => (m && m.row.key === r.key ? { ...m, names: [] } : m)));
      return;
    }
    touch(rows.filter((x) => x.key !== r.key));
  };
  // As in the mockup, removing a tier that clients are on saves at once: they move, each client's
  // History says so, and the tier is gone. Other edits on the page stay unsaved.
  const confirmRemove = () => {
    if (!removing || !saved) return;
    const { row, to } = removing;
    setBusy(true);
    api
      .put<SlidingTier[]>('/sliding-scale/tiers', {
        tiers: saved
          .filter((t) => t.id !== row.id)
          .map((t) => ({ id: t.id, name: t.name, mode: t.mode, amount: t.amount })),
        moves: { [row.id!]: to || null },
      })
      .then((ts) => {
        if (!dirty) reset(ts);
        else {
          setSaved(ts);
          setRows((rs) =>
            rs
              .filter((x) => x.key !== row.key)
              .map((x) => {
                const s = ts.find((t) => t.id === x.id);
                return s ? { ...x, clients: s.clients ?? 0 } : x;
              }),
          );
        }
        setRemoving(null);
        window.dispatchEvent(new Event(SLIDING_TIERS_CHANGED));
        say(`${row.name} removed. ${row.clients} client${row.clients === 1 ? '' : 's'} moved.`);
      })
      .catch((e: unknown) => say(errText(e, 'Could not remove the tier.')))
      .finally(() => setBusy(false));
  };

  const validate = () => {
    const e: Record<string, { name?: string; amount?: string }> = {};
    const seen = new Map<string, string>();
    for (const r of rows) {
      const n = r.name.trim();
      const a = Number(r.amount);
      if (!n) e[r.key] = { ...e[r.key], name: 'Give the tier a name.' };
      else if (seen.has(n.toLowerCase()))
        e[r.key] = { ...e[r.key], name: 'Another tier already has this name.' };
      seen.set(n.toLowerCase(), r.key);
      if (!(a > 0)) e[r.key] = { ...e[r.key], amount: 'Enter an amount more than 0.' };
      else if (r.mode === 'pct' && a > 100)
        e[r.key] = { ...e[r.key], amount: 'A percent can be 100 at most.' };
    }
    setErrs(e);
    const first = rows.find((r) => e[r.key]);
    if (first) {
      const which = e[first.key]!.name ? 'name' : 'amt';
      setTimeout(() => document.getElementById(`ss-tier-${which}-${first.key}`)?.focus(), 30);
    }
    return !first;
  };

  const save = (): Promise<boolean> => {
    if (!validate()) return Promise.resolve(false);
    setBusy(true);
    return api
      .put<SlidingTier[]>('/sliding-scale/tiers', {
        tiers: rows.map((r) => ({
          id: r.id,
          name: r.name.trim(),
          mode: r.mode,
          amount: Number(r.amount),
        })),
      })
      .then((ts) => {
        reset(ts);
        // ehr#375 (Jay 10-30): an open client chart fills in the saved tiers at once
        window.dispatchEvent(new Event(SLIDING_TIERS_CHANGED));
        say('Sliding scale tiers saved.');
        return true;
      })
      .catch((e: unknown) => {
        say(errText(e, 'Could not save the tiers.'));
        return false;
      })
      .finally(() => setBusy(false));
  };
  if (saveRef) saveRef.current = save;

  const openClients = (r: TierRow) => {
    setListing({ name: r.name, clients: null });
    api
      .get<{ contactId: string; name: string; reviewBy: string | null }[]>(
        `/sliding-scale/tiers/${r.id}/clients`,
      )
      .then((c) => setListing({ name: r.name, clients: c }))
      .catch(() => setListing({ name: r.name, clients: [] }));
  };

  if (loadErr)
    return (
      <div className="note warn">
        {loadErr}{' '}
        <button className="btn sm" onClick={load}>
          Try again
        </button>
      </div>
    );
  if (!saved) return <div className="empty">Loading tiers…</div>;
  const today = todayIso();
  const others = removing ? rows.filter((x) => x.key !== removing.row.key && x.id) : [];
  return (
    <div className="ss-tiers">
      {!canEdit && <p className="muted">Only an admin or manager can change the tiers.</p>}
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Tier name</th>
              <th style={{ width: 230 }}>Discount type</th>
              <th style={{ width: 150 }}>Amount</th>
              <th style={{ width: 90 }} className="n">
                Clients
              </th>
              {canEdit && <th style={{ width: 48 }} />}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={canEdit ? 5 : 4} className="muted">
                  No tiers yet.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.key}>
                <td className={errs[r.key]?.name ? 'invalid' : undefined}>
                  <input
                    id={`ss-tier-name-${r.key}`}
                    className="ss-in"
                    value={r.name}
                    disabled={!canEdit}
                    aria-label="Tier name"
                    onChange={(e) => set(r.key, { name: e.target.value })}
                  />
                  {errs[r.key]?.name && <span className="ss-err">{errs[r.key]!.name}</span>}
                </td>
                <td>
                  <Dd
                    value={r.mode}
                    searchable={false}
                    disabled={!canEdit}
                    floating
                    ariaLabel="Discount type"
                    options={MODE_OPTS}
                    onChange={(v) => set(r.key, { mode: v as SlidingMode })}
                  />
                </td>
                <td className={errs[r.key]?.amount ? 'invalid' : undefined}>
                  <input
                    id={`ss-tier-amt-${r.key}`}
                    className="ss-in num"
                    inputMode="decimal"
                    value={r.amount}
                    disabled={!canEdit}
                    aria-label="Amount"
                    placeholder={r.mode === 'usd' ? '$0.00' : '%'}
                    onChange={(e) => set(r.key, { amount: e.target.value.replace(/[^0-9.]/g, '') })}
                  />
                  {errs[r.key]?.amount && <span className="ss-err">{errs[r.key]!.amount}</span>}
                </td>
                <td className="n">
                  {r.id && r.clients > 0 ? (
                    <button type="button" className="ss-link" onClick={() => openClients(r)}>
                      {r.clients}
                    </button>
                  ) : (
                    r.clients
                  )}
                </td>
                {canEdit && (
                  <td className="ss-x">
                    <button
                      type="button"
                      className="ss-del"
                      aria-label={`Remove ${r.name || 'this tier'}`}
                      onClick={() => remove(r)}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden>
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <>
          <div className="btn-row ss-tiers-add">
            <button type="button" className="btn" onClick={add}>
              + Add tier
            </button>
          </div>
          <div className="btn-row ss-tiers-foot">
            <span className="muted">{dirty ? 'Unsaved changes' : 'No unsaved changes'}</span>
            <button
              type="button"
              className="btn ghost"
              disabled={!dirty || busy}
              onClick={() => reset(saved)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn primary"
              disabled={!dirty || busy}
              onClick={() => void save()}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </>
      )}
      {removing && (
        <Modal
          title={`Remove ${removing.row.name}?`}
          onClose={() => setRemoving(null)}
          actions={
            <>
              <button type="button" className="btn ghost" onClick={() => setRemoving(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn ss-danger"
                disabled={busy}
                onClick={confirmRemove}
              >
                Move clients and remove
              </button>
            </>
          }
        >
          <p>
            {removing.row.clients} client{removing.row.clients === 1 ? ' is' : 's are'} on{' '}
            {removing.row.name}
            {removing.names?.length ? `: ${removing.names.join(', ')}` : ''}.
          </p>
          <div className="demo-field" style={{ marginTop: 12 }}>
            <span className="demo-field-label">Move them to</span>
            <Dd
              value={removing.to}
              searchable={false}
              ariaLabel="Move them to"
              options={[{ value: '', label: 'None (full fee)' }].concat(
                others.map((x) => ({
                  value: x.id!,
                  label: tierLabel({ name: x.name, mode: x.mode, amount: Number(x.amount) }),
                })),
              )}
              onChange={(v) => setRemoving({ ...removing, to: v })}
            />
          </div>
        </Modal>
      )}
      {listing && (
        <Modal
          title={`Clients on ${listing.name}`}
          onClose={() => setListing(null)}
          actions={
            <button type="button" className="btn primary" onClick={() => setListing(null)}>
              Close
            </button>
          }
        >
          {!listing.clients ? (
            <p className="muted">Loading…</p>
          ) : listing.clients.length === 0 ? (
            <p className="muted">No clients you can see are on this tier.</p>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Review by</th>
                </tr>
              </thead>
              <tbody>
                {listing.clients.map((c) => (
                  <tr key={c.contactId}>
                    <td>
                      <a
                        className="ss-link"
                        href={`/?contact=${encodeURIComponent(c.contactId)}&view=demographics`}
                      >
                        {c.name}
                      </a>
                    </td>
                    <td className={c.reviewBy && today > c.reviewBy ? 'ss-late' : undefined}>
                      {c.reviewBy ? us(c.reviewBy) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal>
      )}
    </div>
  );
}
